import React from 'react';
import { useForm } from 'react-hook-form';
import { X } from 'lucide-react';
import { Vehicle, TraccarDevice } from '../../types';

interface VehicleFormProps {
  vehicle?: Vehicle;
  onSubmit: (data: Partial<Vehicle>) => void;
  onCancel: () => void;
  traccarDevices: TraccarDevice[];
}

const VehicleForm: React.FC<VehicleFormProps> = ({
  vehicle,
  onSubmit,
  onCancel,
  traccarDevices
}) => {
  const { register, handleSubmit, formState: { errors } } = useForm<Partial<Vehicle>>({
    defaultValues: vehicle ? {
      name: vehicle.name,
      registration: vehicle.registration,
      make: vehicle.make,
      model: vehicle.model,
      year: vehicle.year,
      status: vehicle.status,
      traccar_device_id: vehicle.traccar_device_id
    } : {}
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-medium text-fleet-dark">
            {vehicle ? 'Edit Vehicle' : 'Add New Vehicle'}
          </h3>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-fleet-dark mb-1">
              Vehicle Name
            </label>
            <input
              type="text"
              {...register('name', { required: 'Vehicle name is required' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fleet-primary focus:border-transparent"
              placeholder="e.g., Delivery Truck 1"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-fleet-dark mb-1">
              Registration Number
            </label>
            <input
              type="text"
              {...register('registration', { required: 'Registration is required' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fleet-primary focus:border-transparent"
              placeholder="e.g., ABC123GP"
            />
            {errors.registration && (
              <p className="mt-1 text-sm text-red-600">{errors.registration.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-fleet-dark mb-1">
                Make
              </label>
              <input
                type="text"
                {...register('make', { required: 'Make is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fleet-primary focus:border-transparent"
                placeholder="e.g., Toyota"
              />
              {errors.make && (
                <p className="mt-1 text-sm text-red-600">{errors.make.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-fleet-dark mb-1">
                Model
              </label>
              <input
                type="text"
                {...register('model', { required: 'Model is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fleet-primary focus:border-transparent"
                placeholder="e.g., Hiace"
              />
              {errors.model && (
                <p className="mt-1 text-sm text-red-600">{errors.model.message}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-fleet-dark mb-1">
              Year
            </label>
            <input
              type="number"
              {...register('year', { 
                required: 'Year is required',
                min: { value: 1990, message: 'Year must be 1990 or later' },
                max: { value: new Date().getFullYear() + 1, message: 'Invalid year' }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fleet-primary focus:border-transparent"
              placeholder="e.g., 2020"
            />
            {errors.year && (
              <p className="mt-1 text-sm text-red-600">{errors.year.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-fleet-dark mb-1">
              Status
            </label>
            <select
              {...register('status', { required: 'Status is required' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fleet-primary focus:border-transparent"
            >
              <option value="">Select status</option>
              <option value="active">Active</option>
              <option value="maintenance">Maintenance</option>
              <option value="inactive">Inactive</option>
            </select>
            {errors.status && (
              <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-fleet-dark mb-1">
              Traccar Device (Optional)
            </label>
            <select
              {...register('traccar_device_id')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fleet-primary focus:border-transparent"
            >
              <option value="">Select device</option>
              {traccarDevices.map(device => (
                <option key={device.id} value={device.id}>
                  {device.name} ({device.uniqueId})
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-fleet-primary text-white rounded-lg hover:bg-fleet-secondary"
            >
              {vehicle ? 'Update' : 'Add'} Vehicle
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VehicleForm;